import { useState } from 'react';
import PropTypes from 'prop-types';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';


/** Shared Glassmorphic Wrapper */
const GlassWrapper = ({ children }) => (
  <div className="p-4 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 shadow-md transition hover:bg-white/10 dark:bg-white/5">
    {children}
  </div>
);


/**
 * EditableField – Premium text field with inline edit.
 * - Smooth glassmorphism + dark mode
 * - Pencil icon edit trigger
 * - Enter-to-save with loading state
 */
export const EditableField = ({
  label,
  name,
  value = '',
  onSubmit,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [fieldValue, setFieldValue] = useState(value);
  const [loading, setLoading] = useState(false);

  const saveValue = async () => {
    if (fieldValue !== value) {
      setLoading(true);
      await onSubmit({ [name]: fieldValue });
      setLoading(false);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveValue();
    if (e.key === 'Escape') setIsEditing(false);
  };

  return (
    <GlassWrapper>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-gray-300 dark:text-gray-400">{label}</label>
          {!disabled && !isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-gray-400 hover:text-blue-400 transition"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>

        {isEditing ? (
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => setFieldValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={disabled}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
          />
        ) : (
          <p
            className={clsx(
              'text-sm text-white cursor-pointer hover:text-blue-300 transition',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            onClick={() => !disabled && setIsEditing(true)}
          >
            {fieldValue || <em className="text-gray-400">Missing information</em>}
          </p>
        )}

        {isEditing && (
          <p className="text-xs text-gray-400">Press <strong>Enter</strong> to save or <strong>Esc</strong> to cancel.</p>
        )}
      </div>
    </GlassWrapper>
  );
};

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};